import React, { useEffect, useState } from "react";
import { HolidayType } from "./HolidayTypeList";
import {
  Modal,
  ModalBody,
  ModalHeader,
  Row,
  Col,
  FormGroup,
  Label,
  Input,
  Button,
  FormFeedback,
} from "reactstrap";
import { useTranslation } from "react-i18next";

interface Props {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: Omit<HolidayType, "id">, id?: number) => void;
  editData: HolidayType | null;
}

const HolidayTypeFormModal: React.FC<Props> = ({
  open,
  onClose,
  onSubmit,
  editData,
}) => {
  const [typeName, setTypeName] = useState("");
  const [description, setDescription] = useState("");
  const [touched, setTouched] = useState<{ [key: string]: boolean }>({});
  const { t } = useTranslation();

  useEffect(() => {
    if (editData) {
      setTypeName(editData.typeName);
      setDescription(editData.description);
    } else {
      setTypeName("");
      setDescription("");
    }
    setTouched({});
  }, [editData, open]);

  const validate = () => {
    const errors: { [key: string]: string } = {};
    if (!typeName.trim()) errors.typeName = t("TypeNameRequired");
    return errors;
  };

  const errors = validate();
  const isInvalid = (field: string) => !!errors[field] && touched[field];

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setTouched({ typeName: true });
    if (Object.keys(errors).length > 0) return;
    onSubmit({ typeName, description }, editData?.id);
  };

  return (
    <Modal isOpen={open} toggle={onClose} centered>
      <ModalHeader toggle={onClose}>
        {editData ? t("EditHolidayType") : t("AddHolidayType")}
      </ModalHeader>
      <ModalBody>
        <form onSubmit={handleSubmit} className="theme-form">
          <Row>
            <Col sm="12">
              <FormGroup>
                <Label>{t("HolidayType")} *</Label>
                <Input
                  value={typeName}
                  onChange={(e) => setTypeName(e.target.value)}
                  className="form-control"
                  invalid={isInvalid("typeName")}
                  required
                />
                <FormFeedback>{errors.typeName}</FormFeedback>
              </FormGroup>
            </Col>
            <Col sm="12">
              <FormGroup>
                <Label>{t("Description")}</Label>
                <Input
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="form-control"
                  type="textarea"
                />
              </FormGroup>
            </Col>
          </Row>
          <div className="text-end mt-3">
            <Button
              color="secondary"
              className="me-2"
              type="button"
              onClick={onClose}
            >
              {t("Cancel")}
            </Button>
            <Button color="primary" type="submit">
              {editData ? t("Update") : t("Add")}
            </Button>
          </div>
        </form>
      </ModalBody>
    </Modal>
  );
};

export default HolidayTypeFormModal;
